import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type TransitionEvent } from 'react'
import { Icon } from '../ui/Icon'

/**
 * THE TAB CORNER, PUT AWAY — owner, 2026-11-01: Agent Session 裡面的 Browser 瀏覽視窗, 使用者從左向右滑的時候可以將視窗
 * 收納成一個小抽屜, 就像 Notes 跟 Bookmark 一樣.
 *
 * Swiped to the right, the corner's picture leaves by that edge and a handle stays where it was — the notes' and the
 * bookmarks' kind of handle, at the stage's right edge. Tapped, or pulled back to the left, the handle brings it out.
 * Put away on this device for every session, and remembered: a person who puts it away wants the stage clear, not one
 * agent's. Your own agent's corner (AgentTabCorner) and a friend's (friends/SharedTabCorner) alike.
 */
const KEY = 'avg.tabCorner.away'
/** Released past this far to the right, the corner goes away; short of it, it goes back. */
const AWAY_PX = 56
/** Moved further than this, the press was a swipe, not a tap — the sheet does not open. */
const SLOP_PX = 8

function readAway(): boolean {
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

function writeAway(on: boolean): void {
  try {
    if (on) localStorage.setItem(KEY, '1')
    else localStorage.removeItem(KEY)
  } catch {
    /* a private window: put away for this visit */
  }
}

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * Whether the corner is put away, and the swipe on it that puts it away. `swipe` goes on the corner's button: its
 * pointer handlers, and the style that follows the finger. A press that became a swipe opens nothing: `tapped()` says
 * whether the click that follows a press was a tap.
 */
export function useTabTuck() {
  const [away, setAway] = useState(readAway)
  /** Just brought back from the handle: it slides in once (`.tab-corner.back`). */
  const [back, setBack] = useState(false)
  const [dx, setDx] = useState(0)
  const [leaving, setLeaving] = useState(false)
  const from = useRef<{ x: number; y: number; id: number } | null>(null)
  const swiped = useRef(false)

  const putAway = () => {
    from.current = null
    setLeaving(false)
    setDx(0)
    setBack(false)
    setAway(true)
    writeAway(true)
  }
  const bringBack = () => {
    setAway(false)
    writeAway(false)
    setBack(!reducedMotion())
  }

  const swipe = {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0 || leaving) return
      from.current = { x: e.clientX, y: e.clientY, id: e.pointerId }
      swiped.current = false
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const f = from.current
      if (!f || f.id !== e.pointerId) return
      const x = e.clientX - f.x
      if (!swiped.current) {
        if (Math.abs(x) < SLOP_PX) return
        // Up or down first: the page's, not this.
        if (Math.abs(e.clientY - f.y) > Math.abs(x)) {
          from.current = null
          return
        }
        swiped.current = true
        e.currentTarget.setPointerCapture?.(e.pointerId)
      }
      setDx(Math.max(0, x))
    },
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
      const f = from.current
      from.current = null
      if (!f || !swiped.current) return
      if (e.clientX - f.x < AWAY_PX) {
        setDx(0)
        return
      }
      // Out by the right edge, then the handle; at once where nothing moves.
      if (reducedMotion()) putAway()
      else setLeaving(true)
    },
    onPointerCancel: () => {
      from.current = null
      setDx(0)
    },
    onTransitionEnd: (e: TransitionEvent<HTMLElement>) => {
      if (leaving && e.target === e.currentTarget && e.propertyName === 'transform') putAway()
    },
    onAnimationEnd: () => setBack(false),
    style: (leaving
      ? { transform: 'translateX(calc(100% + 24px))', opacity: 0, transition: 'transform 0.18s ease-in, opacity 0.18s ease-in' }
      : dx
        ? { transform: `translateX(${dx}px)`, opacity: Math.max(0.35, 1 - dx / 240), transition: 'none' }
        : undefined) as CSSProperties | undefined,
  }

  /** The click after a press: false when that press was a swipe (and forgets it). */
  const tapped = () => {
    const was = swiped.current
    swiped.current = false
    return !was
  }

  return { away, back, swipe, tapped, bringBack }
}

/**
 * The put-away corner: a handle at the stage's right edge where the corner was — the notes' kind (pull it to the left,
 * or tap it), in the corner's own colour.
 */
export function TabHandle({ label, onOut }: { label: string; onOut: () => void }) {
  const x = useRef(0)
  return (
    <button
      type="button"
      className="tab-handle"
      aria-label={`${label} — pull to open`}
      title="Pull out the browser tab"
      onClick={onOut}
      onTouchStart={(e) => {
        x.current = e.touches[0]?.clientX ?? 0
      }}
      onTouchMove={(e) => {
        const now = e.touches[0]?.clientX ?? 0
        if (x.current - now > 8) {
          x.current = Number.NEGATIVE_INFINITY // once per gesture
          onOut()
        }
      }}
    >
      <Icon name="tab" size={11} />
    </button>
  )
}
